import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import moment from "moment";
import { ConfigContext } from "../security/ConfigContext";
import useSecurity from "../hooks/useSecurity";
import UserUtils from "../utils/UserUtils";
import _ from "lodash";
import DashboardMode from "../constants/DashboardMode";
import { getItem, addItem } from "../utils/AppLocalStorage";
import ConfigUtils from "../utils/ConfigUtils";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  draftFilter,
  favFilter,
} from "../components/filters/PresetFilters";
import { UserContext } from "./UserContext";
import { useHistory, useLocation } from "react-router-dom";
import useAxios from "../adapter/useAxios";
import { SocketContext } from "./SocketContext";
import SocketUtils from "../utils/SocketUtils";
import { PERMITTED_ROLES } from "../constants/UserRoles";
import { DATE_TYPE_OPTIONS } from "../constants/DateConstants";
import { AOC_DELIVERIES } from "../constants/CreateMediaRequest";

export enum Action {
  STATUS = "status",
  ASSIGNEE = "assignee",
  BEGINQC = "qcList",
  QCDONE = "qcDone",
  RESUMEWORKFLOW = "resumeWorkflow",
  DUEDATE = "dueDate"
}

const RequestFilterTransform = {
  status: "rolledUpStatus",
  jobType: "jobType",
  idType: "sourceType",
  "requestor.deptName": "requestor.deptName",
  myDrafts: "myDrafts",
  myFavorite: "myFavorite",
  myTeamData: "myTeamData"
};

const JobFilterTransform = {
  status: "status",
  jobType: "jobType",
  idType: "source.sourceLocation.locationSubType",
  "requestor.deptName": "requestSummary.deptName",
  assignedTo: "assignedTo.oktaUid",
  delivery: "delivery",
  nmo: "nmo",
  pacs: "pacs",
  encoding: "encoding",
  assignedToMe: "assignedToMe",
  myTeamData: "myTeamData"
};

const RequestSortTransform = {
  dueDate: "rolledUpDate",
  requestName: "name",
  department: "requestor.deptName",
  requestId: "requestId",
  jobType: "jobType",
  sourceType: "sourceType",
  requestor: "requestor.fullName",
  status: "rolledUpStatus",
  dateSubmitted: "submittedDate",
  jobCount: "totalJobCount",
  contentType: "contentType",
  rolledUpCompletedDate: "rolledUpCompletedDate"
};

const JobSortTransform = {
  dueDate: "dueDate",
  requestName: "requestSummary.name",
  department: "requestSummary.deptName",
  requestId: "requestSummary.requestId",
  jobId: "jobId",
  jobType: "jobType",
  sourceType: "source.sourceLocation.locationSubType",
  requestor: "requestSummary.requestorFullName",
  status: "status",
  assignedTo: "assignedTo.sAMAccountName",
  dateSubmitted: "requestSummary.submittedDate",
  preAir: "source.preAir",
  fileType: "processor.profile.description",
  contentType: "contentType",
  dateTimeCompleted: "completedDate"
};

const MSC_SOURCE_LOCATIONS = {
  mediaSupplyChain: ['contentApp', 'keystone', 'sdviTurner', 'other', 'discovery', 'switchboard'],
  newIdType : 'source.sourceLocation.newLocationSubType'
}

const initialState = {
  loading: true,
  sort: {
    [DashboardMode.REQUESTS]: {
      dateSubmitted: "desc",
    },
    [DashboardMode.JOBS]: {
      dueDate: "desc",
    },
  },
  jobs: [],
  requests: [],
  totalPages: 0,
  currentPage: 1,
  rowsPerPage: 25,
  filter: {},
  downloading: false,
  dashboardView: DashboardMode.JOBS,
  _setCurrentPage: (_value) => { },
  _setRowsPerPage: (_value) => { },
  updateRecords: (
    _action: Action,
    _recordIds,
    _data,
    _qcCallback?: () => void,
    _qcValidated?: boolean,
    _updateSelectionCallback?: (data: any) => void,
    _isWorkflowCancelled?: boolean,
    _isHoldFlow?: boolean,
    _isExternal?: boolean
  ) => { },
  deleteJob: (_recordId) => { },
  _setDashboardView: (_view) => { },
  _setFilter: (_query, _isPresetFilter: boolean = false) => { },
  getFilter: (_key): Array<any> => {
    return [];
  },
  _setSort: (_sortingObj) => { },
  _getSort: () => { },
  showLoading: () => { },
  selectedColumnAction: {},
  _setColumnAction: (_action) => { },
  isLoading: (): Boolean => {
    return false;
  },
  updateNotes: (_recordId, _data) => { },
  updateFavoritesRequestsIds: (_favorite, _requestId) => { },
  fetchReport: () => { },
  alertMessage: "",
  _setDownloading: (_flag) => { },
  enableSnackbar: false,
  _setEnableSnackBar: (_flag) => { },
  jobDateType: "SUBMITTED_DATE",
  requestDateType: "SUBMITTED_DATE",
  _setJobDateType: (_flag) => { },
  _setRequestDateType: (_flag) => { },
};

export const DashboardContext = createContext(initialState);

export const DashboardContextProvider = ({ children }) => {
  const deliveryMapping = ConfigUtils.getDeliveryMapping();
  const PresetFilters = ConfigUtils.getPresetFilters();
  const transferRegions = ConfigUtils.getTransferRegions();
  const location = useLocation();
  const history = useHistory();
  const [dashboardView, setDashboardView] = useState(
    initialState.dashboardView
  );
  const [config] = useContext(ConfigContext);
  const { isAuthenticated, getAccessToken, userInfo } = useSecurity();
  const [jobs, setJobs] = useState(initialState.jobs);
  const [requests, setRequests] = useState(initialState.requests);
  const [totalPages, setTotalPages] = useState(initialState.totalPages);
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialState.rowsPerPage);
  const [filter, setFilter] = useState(initialState.filter);
  const [sort, setSort] = useState(initialState.sort);
  const previousView = usePrevious(dashboardView);
  const previousSort = usePrevious(sort);
  const previousFilter = usePrevious(filter);
  const [loading, setLoading] = useState(true);
  const [localStorageFetched, setLocalStorageFetched] = useState(false);
  const [selectedColumnAction, setColumnAction] = useState(
    initialState.selectedColumnAction
  );
  const { favoriteRequestsIds, _setFavoritesRequestsIds } =
    useContext(UserContext);
  const [enableSnackbar, setEnableSnackbar] = useState(false);
  const [downloading, setDownloading] = useState(initialState.downloading);
  const [alertMessage, setAlertMessage] = useState(initialState.alertMessage);
  const [jobDateType, setJobDateType] = useState(initialState.jobDateType);
  const [requestDateType, setRequestDateType] = useState(initialState.requestDateType);
  const {messageFromSocket, _setMessageToSocket, _setMessageFromSocket} = useContext(SocketContext);
  const {post, put, deleteReq} = useAxios();
  let user = UserUtils.transformUserInfo(userInfo);

  const _setDownloading = (flag) => {
    setDownloading(flag);
  };

  const _setEnableSnackBar = (flag) => {
    setEnableSnackbar(flag);
  };
  const _setColumnAction = (action) => {
    setColumnAction(action);
  };

  const _setCurrentPage = (value) => {
    setCurrentPage(value);
  };

  const _setJobDateType = (value) => {
    setJobDateType(value);
  };

  const _setRequestDateType = (value) => {
    setRequestDateType(value);
  };

  const showLoading = () => {
    setLoading(true);
  };

  const isLoading = () => {
    return loading;
  };

  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const _setRowsPerPage = (value) => {
    const userId = _.get(userInfo, "sAMAccountName", "");
    addItem(userId, `${dashboardView}.rowsPerPage`, value.toString());
    setRowsPerPage(value);
  };

  const _setFilter = (query, _isPresetFilter: boolean = false) => {
    let clonnedQuery = { ...filter };
    clonnedQuery[dashboardView] = query;
    addItem(_.get(userInfo, "sAMAccountName"), "filters", clonnedQuery);
    history.push("/");
    setFilter(clonnedQuery);
  };

  const getFilter = (key) => {
    try {
      let prefData = getItem(_.get(userInfo, "sAMAccountName"), "filters");
      let filtersValue = prefData[dashboardView][key];
      return filtersValue instanceof Array
        ? filtersValue
        : filtersValue.split(",");
    } catch (e) {
      return [];
    }
  };

  const _setDashboardView = (view) => {
    const userId = _.get(userInfo, "sAMAccountName", "");
    setRowsPerPage(
      parseInt(getItem(userId, `${view}.rowsPerPage`)) ||
        initialState.rowsPerPage
    );
    setCurrentPage(initialState.currentPage);
    localStorage.removeItem("lastViewedJobId");
    if (view === dashboardView) {
      return;
    }
    showLoading();
    addItem(_.get(userInfo, "sAMAccountName"), "dashboardView", view);
    setDashboardView(view);
  };

  const composeFilterQuery = () => {
    let filterObj = {};
    let filterValue = _.get(
      getItem(_.get(userInfo, "sAMAccountName"), "filters"),
      dashboardView,
      {}
    );
    if (!_.isEmpty(filterValue)) {
      let keys = _.keys(filterValue);
      _.map(keys, (key) => {
        let data = _.get(filterValue, key);
        if (["fromDate", "toDate", "textSearch", "dateField"].includes(key)) {
          filterObj[key] = data;
        } else {
          if (dashboardView === DashboardMode.JOBS) {
            let keyVal = _.get(JobFilterTransform, key);
            filterObj[keyVal] = isPresetFilter(keyVal)
              ? data
              : data instanceof Array
              ? data
              : data.split(",");
          } else {
            let keyVal = _.get(RequestFilterTransform, key);
            filterObj[keyVal] = isPresetFilter(keyVal)
              ? data
              : data instanceof Array
              ? data
              : data.split(",");
          }
        }
      });
    }
    return filterObj;
  };

  const isPresetFilter = (key) => {
    let allFilters = [...PresetFilters] as any;
    if (dashboardView === DashboardMode.REQUESTS) {
      allFilters.push(draftFilter);
      allFilters.push(favFilter);
    }
    return _.includes(
      _.map(allFilters, (pf) => _.keys(_.get(pf, "field"))).flat(),
      key
    );
  };

  const updateFavoritesRequestsIds = (favorite, requestId) => {
    user = UserUtils.transformUserInfo(userInfo);
    if (favorite) {
      let index = favoriteRequestsIds.indexOf(requestId);
      if (index > -1) {
        favoriteRequestsIds.splice(index, 1);
      }
    } else {
      favoriteRequestsIds.push(requestId);
    }
    let favoriteN = {};
    favoriteN["favoriteRequestsIds"] = favoriteRequestsIds;
    setLoading(true);
    put({
      url: config.apiUrls.users + _.get(user, "oktaUid"),
      data: favoriteN
    })
    .then((response) => {
      _setFavoritesRequestsIds(
        (response && response.favoriteRequestsIds) || []
      );
      let clonnedQuery = { ...filter };
      if (_.has(clonnedQuery[dashboardView], "myFavorite")) {
        _setFilter({ myFavorite: favoriteRequestsIds }, true);
      }
      fetchData();
    })
    .catch((error) => {
      console.log(error)
    })
    .finally(() => {
      setLoading(false);
    })
  };

  const composeSortingQuery = () => {
    let sortObj = {};
    let sortingValue = _.get(
      getItem(_.get(userInfo, "sAMAccountName"), "sorting"),
      dashboardView,
      {}
    );
    if (!_.isEmpty(sortingValue)) {
      let keys = _.keys(sortingValue);
      _.map(keys, (key) => {
        let data = _.get(sortingValue, key);
        if (dashboardView === DashboardMode.JOBS) {
          sortObj[_.get(JobSortTransform, key)] = data === "asc" ? 1 : -1;
        } else {
          sortObj[_.get(RequestSortTransform, key)] = data === "asc" ? 1 : -1;
        }
      });
    }
    return sortObj;
  };
  const fetchReport = () => {
    if (totalPages === 0) {
      setEnableSnackbar(true);
      setAlertMessage("Unable to export as no data is available");
      return;
    }
    setDownloading(true);
    if (isAuthenticated) {
      user = UserUtils.transformUserInfo(userInfo, true);
      let filterQuery = {
        query: composeFilterQuery(),
        sort: composeSortingQuery(),
        dashboardView: dashboardView,
      };
      const idTypeKey = JobFilterTransform.idType;
      const idTypeValue = _.get(filterQuery.query, idTypeKey);
      if (idTypeValue?.includes("DISCOVERY") && !idTypeValue?.includes("discovery") && 
        _.includes(PERMITTED_ROLES.MODAL_SOURCE_LOCATION_ROLES, _.get(userInfo, "role", ''))) {
        idTypeValue.push("discovery");
      }
      if (
        _.has(sort[dashboardView], "jobId") ||
        _.has(sort[dashboardView], "requestId") ||
        _.has(sort[dashboardView], "requestName")
      ) {
        filterQuery["collation"] = {
          locale: "en",
          numericOrdering: true,
          strength: 1,
        };
      }
      let needMailAttachment = totalPages > config.app.maxRecordSizeToExport;
      filterQuery["needMailAttachment"] = needMailAttachment;
      if (_.isEmpty(_.get(filterQuery, "sort"))) {
        delete filterQuery.sort;
      }

      if (idTypeValue) {
      const values = [].concat(idTypeValue);

      const [mscValues, otherValues] = _.partition(values, v =>
        _.includes(MSC_SOURCE_LOCATIONS.mediaSupplyChain, v)
      );

      // Remove old idTypeKey for new MSC Source Type
      filterQuery.query = _.omit(filterQuery.query, idTypeKey);

      if (!_.isEmpty(mscValues)) {
        filterQuery.query[MSC_SOURCE_LOCATIONS.newIdType] = mscValues;
      }

      if (!_.isEmpty(otherValues)) {
        filterQuery.query[idTypeKey] = otherValues;
      }
    }
      //Added code to transform delivery type using delivery mapping file
      if (_.has(filterQuery.query, JobFilterTransform.delivery)) {
        let output = [];
        let delValueArray = _.get(
          filterQuery.query,
          JobFilterTransform.delivery
        );
        _.map(delValueArray, (delVal) => {
          let mappingObj = _.find(deliveryMapping, (del) => {
            return _.get(del, "label") === delVal;
          });
          let mapping = _.get(mappingObj, "destination");
          if (mapping.type !== "Manual" && mapping.type !== "Screeners.com") {
            delete mapping.type;
          }
          output.push(mapping);
        });
        filterQuery.query[JobFilterTransform.delivery] = output;
      }
      filterQuery["user"] = user;      
      post({
        url: "reporting",
        data: filterQuery
      })
      .then((response) => {        
        if (needMailAttachment) {
          setDownloading(false);
          setAlertMessage(
            "Your export request will be sent to your email shortly"
          );
          setEnableSnackbar(true);
        } else {
          fileDownload(response);
        }
      })
      .catch((err) => {
        console.log(err);
        if (err) {
          setDownloading(false);
          setAlertMessage("Unable to generate report at this time");
          setEnableSnackbar(true);
        }
      })
    }
  };

  const fileDownload = (response) => {
    setDownloading(false);
    if (!_.isEmpty(response.url)) {
      let url = _.get(response, "url");
      let reportName = _.get(response, "reportName");
      let link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", reportName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const fetchData = () => {
    user = UserUtils.transformUserInfo(userInfo, true);
    let filterQuery = {
      query: composeFilterQuery(),
      sort: composeSortingQuery(),
    };
    const idTypeKey = JobFilterTransform.idType;
    const idTypeValue = _.get(filterQuery.query, idTypeKey);
    if (idTypeValue?.includes("DISCOVERY") && !idTypeValue?.includes("discovery") && 
    _.includes(PERMITTED_ROLES.MODAL_SOURCE_LOCATION_ROLES, _.get(userInfo, "role", ''))) {
      idTypeValue.push("discovery");
    }
    if (
      _.has(sort[dashboardView], "jobId") ||
      _.has(sort[dashboardView], "requestId") ||
      _.has(sort[dashboardView], "requestName")
    ) {
      filterQuery["collation"] = {
        locale: "en",
        numericOrdering: true,
        strength: 1,
      };
    }
    if (_.isEmpty(_.get(filterQuery, "sort"))) {
      delete filterQuery.sort;
    }

    if (idTypeValue) {
      const values = [].concat(idTypeValue);

      const [mscValues, otherValues] = _.partition(values, v =>
        _.includes(MSC_SOURCE_LOCATIONS.mediaSupplyChain, v)
      );

      // Remove old idTypeKey for new MSC Source Type
      filterQuery.query = _.omit(filterQuery.query, idTypeKey);

      if (!_.isEmpty(mscValues)) {
        filterQuery.query[MSC_SOURCE_LOCATIONS.newIdType] = mscValues;
      }

      if (!_.isEmpty(otherValues)) {
        filterQuery.query[idTypeKey] = otherValues;
      }
    }

    if (_.has(filterQuery.query, JobFilterTransform.delivery)) {
      let output = [];
      let delValueArray = _.get(
        filterQuery.query,
        JobFilterTransform.delivery
      );
      _.map(delValueArray, (delVal) => {
        let mappingObj = _.find(deliveryMapping, (del) => {
          return _.get(del, "label") === delVal;
        });
        let mapping = _.get(mappingObj, "destination");
        if (mapping.locationType === "WBDShare" || mapping.locationType === "CS-DMG Promos") {
          mapping.locationType = "morph-to-wbd-share-aoc-bucket";
          mapping.locationSubType = "morph-to-wbd-share-aoc-bucket";
        }
        if (mapping.locationType === "WMS Intl CS") {
          mapping.locationType = "wms-intl-cs-group";
          mapping.locationSubType = "wms-intl-cs-group";
        }
        if (mapping.locationType === "DETE") {
          mapping.locationType = "dete";
          mapping.locationSubType = "dete";
        }
        if (mapping.locationType === "BBFC Max UK") {
          mapping.locationType = "bbfc-max-uk";
          mapping.locationSubType = "bbfc-max-uk";
        }
        if(mapping.locationType === "DP VAULT") {
          mapping.locationType = "dp-vault";
          mapping.locationSubType = "dp-vault";
        }
        if(mapping.locationType === "HBO Aoc") {
          const transferRegion = _.find(transferRegions, (region) => {
            return region.name === delVal
          });
          mapping.locationType = transferRegion?.configId;
          mapping.locationSubType = transferRegion?.configId;
          mapping.dropboxName = transferRegion?.dropboxName;
        }
        if (mapping.type !== "Manual" && mapping.type !== "Screeners.com") {
          delete mapping.type;
        }
        output.push(mapping);
        if (_.includes(AOC_DELIVERIES, delVal)) {
          const dropboxname = _.find(transferRegions, (region) => {
            return region.name === delVal
          })?.dropboxName;
          const newRegion = {
            locationType: "wbdshare-aoc",
            locationSubType: "wbdshare-aoc",
            dropboxName: dropboxname,
            path: mapping.path
          }
          output.push(newRegion);
        }
      });
      filterQuery.query[JobFilterTransform.delivery] = output;
    }
    filterQuery["user"] = user;
    post({
      url:  dashboardView + "?pageNumber=" + currentPage + "&size=" + rowsPerPage,
      data: filterQuery,
      signal: true
    })
    .then((response) => {
      let totalCount = _.get(response, "totalCount");
      handleResponseData(
        _.get(response, "data", ""),
        _.get(totalCount[0], "count", 0),
        _.get(filterQuery, "sort")
      )
    })
    .catch((error) => {
      console.log(error)
    })
    .finally(() => {
      setLoading(false);
    })
  };

  const dueDateSort = (item) => {
    let dueTime = _.get(item, "dueTime");
    return (
      moment(_.get(item, "dueDate")).startOf("day").valueOf() +
      (dueTime
        ? {
            Morning: 1,
            Afternoon: 2,
            Evening: 3,
          }[dueTime]
        : 0)
    );
  };

  const handleResponseData = (records, totalCount, sortData) => {
    const getRequest = records;
    if (dashboardView === DashboardMode.JOBS) {
      const dueDateSortValue = _.get(sortData, "dueDate");
      if (dueDateSortValue) {
        records = records.sort(
          (a, b) => (dueDateSort(a) - dueDateSort(b)) * dueDateSortValue
        );
      }
      setJobs(records);
      setRequests([]);
    } else {
      setJobs([]);
      setRequests(getRequest);
    }
    setTotalPages(totalCount);
  };

  useEffect(() => {
    let userId = _.get(userInfo, "sAMAccountName", "");
    if (!_.isEmpty(userId)) {
      let _dashboardView = getItem(userId, "dashboardView");
      let _filter = getItem(userId, "filters");
      let _sorting = getItem(userId, "sorting");
      if (!_.isEmpty(_dashboardView)) {
        setRowsPerPage(
          parseInt(getItem(userId, `${_dashboardView}.rowsPerPage`)) ||
            initialState.rowsPerPage
        );
        setDashboardView(_dashboardView);
      } else {
        setRowsPerPage(
          parseInt(getItem(userId, `${dashboardView}.rowsPerPage`)) ||
            initialState.rowsPerPage
        );
        addItem(userId, "dashboardView", dashboardView);
      }
      if (!_.isEmpty(_filter)) {
        setFilter(_filter);
      }
      if (!_.isEmpty(_sorting)) {
        setSort(_sorting);
      } else {
        addItem(userId, "sorting", initialState.sort);
      }
      setLocalStorageFetched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  useEffect(() => {
    const userId = _.get(userInfo, "sAMAccountName", "");
    if (!_.isEmpty(userId)) {
      const dashboardView = getItem(userId, "dashboardView");
      const filters = getItem(userId, "filters");
      const storedDateField = _.get(filters, `${dashboardView}.dateField`);
      const options = DATE_TYPE_OPTIONS[dashboardView] || [];
      const match = _.find(options, { field: storedDateField });
      const value =  match?.key;
      if (value) {
        if (dashboardView === DashboardMode.JOBS) {
          setJobDateType(value);
        } else if (dashboardView === DashboardMode.REQUESTS) {
          setRequestDateType(value);
        }
      } else {
        setJobDateType(initialState.jobDateType);
        setRequestDateType(initialState.requestDateType);
      }
    }
  }, [filter, dashboardView, userInfo]);

  useEffect(() => {
    if (isAuthenticated && localStorageFetched&&!_.isEmpty(_.get(user,"role"))) {
      let pageChanged = false;
      let isSortChanged = JSON.stringify(previousSort) !== JSON.stringify(sort);
      let isFilterChanged =
        JSON.stringify(previousFilter) !== JSON.stringify(filter);
      if (previousView !== dashboardView) {
        const pageNumber = new URLSearchParams(location.search).get("pageNum");
        setCurrentPage(pageNumber ? parseInt(pageNumber) : 1);
        setDashboardView(dashboardView);
      } else {
        if (isSortChanged || isFilterChanged) {
          if (isSortChanged) {
            setSort(Object.assign({}, sort));
          }
          if (isFilterChanged) {
            setFilter(Object.assign({}, filter));
          }
          //Set the current page & rows per page to initial state
          const pageNumber = new URLSearchParams(location.search).get(
            "pageNum"
          );
          setCurrentPage(pageNumber ? parseInt(pageNumber) : 1);
          pageChanged = true;
        }
      }
      if (!pageChanged) {
        fetchData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    getAccessToken,
    currentPage,
    rowsPerPage,
    dashboardView,
    sort,
    filter,
    localStorageFetched
  ]);

  const deleteJob = (recordId) => {
    if (!_.isEmpty(recordId)) {
      setLoading(true);
      deleteReq({
        url: config.apiUrls.requests + recordId
      })
      .then(() => {
        fetchData();
      })
      .catch((error) => {
        console.log(error)
      })
      .finally(() => {
        setLoading(false);
      })
    }
  };

  // Need to write logic for this function
  const updateRecords = (
    action: Action,
    recordIds,
    data,
    qcCallback?,
    qcValidated = false,
    updateSelectionCallback?,
    isWorkflowCancelled = false,
    isHoldFlow = false,
    isExternal = false
  ) => {
    if (_.isEmpty(recordIds) || _.isEmpty(action) || _.isEmpty(data)) {
      return;
    }
    let isMultiple = _.size(recordIds) > 1 ? true : false;
    let requestUrl = isMultiple
      ? config.apiUrls.jobs + "updateMultiple"
      : config.apiUrls.jobs + _.first(recordIds);

    if (isMultiple) {
      data["id"] = recordIds;
    }
    user = UserUtils.transformUserInfo(userInfo, true);
    data["user"] = user;
    setLoading(true);
    if (isExternal) {
      return put({
        url: config.apiUrls.jobs + recordIds + "/status",
        data: data
      })
      .then((response) => {
        if (updateSelectionCallback) {
          updateSelectionCallback(_.first(response));
        }
        fetchData();
        if (qcCallback) {
          qcCallback();
        }
      })
      .catch((error) => {
        console.log(error)
      })
      .finally(() => {
        setLoading(false);
      })
    } else if (isWorkflowCancelled || isHoldFlow) {
      const automationUrl = config.apiUrls.jobs + recordIds + "/status";
      const automationCall = () => {
        return put({
          url: automationUrl,
          data: {
            status: isHoldFlow ? "hold" : "cancel",
            user: user
          }
        }) 
        .then(() => {
          fetchData();
        })
        .catch((error) => {
          console.log(error)
        })
        .finally(() => {
          setLoading(false);
        })
      };
      automationCall();
    } else {
      if (
        action === Action.STATUS ||
        action === Action.ASSIGNEE ||
        action === Action.BEGINQC ||
        action === Action.QCDONE||
        action === Action.DUEDATE
      ) {
        if (action === Action.QCDONE && qcValidated) {
          data["status"] = "WF_QC_DONE";
        }
        return put({
          url:  requestUrl,
          data:  data
        })
        .then((response) => {
          if (updateSelectionCallback) {
            updateSelectionCallback(_.first(response));
          }
          const socketMessage = SocketUtils.buildSocketMessage('JOB_DASHBOARD');
          _setMessageToSocket(socketMessage);
          if (qcCallback) {
            qcCallback();
          }
        })
        .catch((error) => {
          console.log(error)
        })
        .finally(() => {
          setLoading(false);
        })
      }
    }
  };
  
  const updateNotes = (recordId, data) => {
    if (_.isEmpty(recordId)) {
      return;
    }
    put({
      url: config.apiUrls.jobs + _.first(recordId) + "/notes",
      data: data
    })
    .then(() => {
      fetchData();
    })
    .catch((error) => {
      console.log(error)
    })
  };

  const _getSort = () => {
    return _.get(sort, dashboardView, {});
  };

  const _setSort = (sortingObj) => {
    let clonnedSorting = { ...sort };
    clonnedSorting[dashboardView] = sortingObj;
    addItem(_.get(userInfo, "sAMAccountName"), "sorting", clonnedSorting);
    setCurrentPage(1);    
    setSort(clonnedSorting);
  };
  
  useEffect(() => {
    if (messageFromSocket && SocketUtils.isDashboardOrSummaryRefresh(messageFromSocket)) {
      console.log('Refreshed..')
      fetchData();
      _setMessageFromSocket("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [messageFromSocket]);

  return (
    <DashboardContext.Provider
      value={{
        loading,
        isLoading,
        sort,
        jobs,
        requests,
        totalPages,
        currentPage,
        rowsPerPage,
        filter,
        dashboardView,
        updateRecords,
        _setCurrentPage,
        _setRowsPerPage,
        deleteJob,
        _setDashboardView,
        _setFilter,
        _getSort,
        getFilter,
        _setSort,
        showLoading,
        selectedColumnAction,
        _setColumnAction,
        updateNotes,
        updateFavoritesRequestsIds,
        fetchReport,
        downloading,
        alertMessage,
        _setDownloading,
        enableSnackbar,
        _setEnableSnackBar,
        jobDateType,
        _setJobDateType,
        requestDateType,
        _setRequestDateType
      }}
    >
      {children}
      <LoadingSpinner show={loading} />
    </DashboardContext.Provider>
  );
};